import {
  dateToEpochSeconds,
  dateToEpochSecondsNullable,
  epochSecondsToDate,
  epochSecondsToDateNullable,
  stripUndefined,
  z,
} from '@customerio/hydra-validation';
import type {
  Campaign as CampaignModel,
  CampaignsProfileMatchTimeRequest,
  operations,
  PreviewsObjectDatePreviewRequest,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

// Generated query params for the campaigns list op. `state` and `sort` are
// constrained to enums in the spec, so deriving them here keeps the params in
// sync with the API instead of widening back to `string`.
type CampaignsListQuery = NonNullable<
  operations['get-v1-environments-environment_id-campaigns']['parameters']['query']
>;

/**
 * Campaign entity — schemas, type, and entity bundle.
 *
 * **Type derivation, not field redeclaration.** The `Campaign` type is
 * the generated `Campaign` model with date fields swapped for `Date`
 * instances. Adding/removing campaign fields on the backend means
 * regenerating `@customerio/hydra-ui-api` — this file follows
 * automatically.
 *
 * The Zod schemas (`CampaignFromApi`, `CampaignToApi`) declare ONLY the
 * fields that need transformation (epoch seconds ↔ Date). `looseObject`
 * preserves every other field at runtime; a pass-through `z.custom` at the end
 * of the pipe declares the `Campaign` shape the loose output stands for.
 *
 * If we ever need real validation (a field that's required on the wire
 * but optional in TS, an enum we want to assert at runtime, …), declare
 * it on the schema next to the transforms — adds Zod validation
 * incrementally without forcing every other field to be repeated.
 */

/* ------------------------------------------------------------------------- */
/* Type — derived from the API type                                           */
/* ------------------------------------------------------------------------- */

export type Campaign = Omit<CampaignModel, 'created' | 'updated' | 'first_started'> & {
  created: Date;
  updated: Date;
  first_started: Date | null;
};

/** What `serialize()` returns — the wire shape, with only the fields the caller set. */
type CampaignWireWritePayload = Partial<CampaignModel>;

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

/**
 * Wire → entity. `looseObject` preserves all unmentioned fields; we
 * declare only the date transforms. The trailing pass-through `z.custom`
 * bridges Zod's loose-object output (which types unmentioned fields as
 * `unknown`) back to the `Campaign` shape inferred from the generated model.
 *
 * No FK-id coercions here: `tag_ids` and `subscription_topic_id` ship
 * from the wire as strings (`string[]` and `string | null` respectively,
 * per the generated `Campaign`), and every entity in this layer uses string
 * ids — so the wire shape is already the entity shape. The
 * number→string coercion lives on `Tag.id` / `SubscriptionTopic.id`,
 * not on the FK arrays referencing them.
 */
const CampaignFromApi = z
  .looseObject({
    created: epochSecondsToDate,
    updated: epochSecondsToDate,
    first_started: epochSecondsToDateNullable,
  })
  // `looseObject` keeps every undeclared field at runtime but types it as
  // `unknown`. Piping through a check-free `z.custom` is a runtime no-op that
  // declares the parsed shape, so the schema states its output type rather
  // than having one asserted onto it.
  .pipe(z.custom<Campaign>());

/**
 * Entity → wire. Inverse direction. Strips undefined fields so the API
 * body only contains what the caller set.
 */
const CampaignToApi = z
  .looseObject({
    created: dateToEpochSeconds.optional(),
    updated: dateToEpochSeconds.optional(),
    first_started: dateToEpochSecondsNullable.optional(),
  })
  .transform(stripUndefined)
  // Same reason as `CampaignFromApi`: declare the wire payload shape through a
  // pass-through `z.custom` instead of asserting it.
  .pipe(z.custom<CampaignWireWritePayload>());

/* ------------------------------------------------------------------------- */
/* List params + relations                                                    */
/* ------------------------------------------------------------------------- */

export interface CampaignListParams {
  page: number;
  state?: CampaignsListQuery['state'];
  type?: string;
  /** Server-side name search term. */
  search?: string;
  searchTypes?: string[];
  subscriptionTopicId?: number;
  sendToUnsubscribed?: boolean;
  sort?: CampaignsListQuery['sort'];
  asc?: boolean;
}

/**
 * Filters on the Broadcasts API-triggered tab.
 *
 * Kept separate from {@link CampaignListParams} because the two reads send
 * genuinely different requests, not the same request with more filters:
 * `getCampaigns` pins `version: '2'` and `excludeMetrics: true`, while this
 * tab sends neither. It renders `sent` and `delivered` straight off each row,
 * and both params select different metrics code paths server-side.
 *
 * Id arrays are strings, as everywhere in this layer, and are converted to
 * the numbers the wire wants when the query is built.
 */
export interface ApiTriggeredBroadcastListParams {
  page: number;
  state?: CampaignsListQuery['state'];
  search?: string;
  sort?: CampaignsListQuery['sort'];
  asc?: boolean;
  sendToUnsubscribed?: boolean;
  subscriptionTopicId?: number;
  tagIds?: readonly string[];
  objectTypeIds?: readonly string[];
  goalIds?: readonly string[];
}

/** Relation declarations for Campaign. Both kinds: `belongs-to` and `has-many`. */
type CampaignRelations = {
  readonly actions: {
    readonly kind: 'has-many';
    readonly target: 'action';
    readonly via: { readonly on: 'target'; readonly field: 'campaign_id' };
  };
  readonly tags: {
    readonly kind: 'has-many';
    readonly target: 'tag';
    readonly via: { readonly on: 'self'; readonly field: 'tag_ids' };
  };
  readonly subscription_topic: {
    readonly kind: 'belongs-to';
    readonly target: 'subscription_topic';
    readonly via: 'subscription_topic_id';
  };
};

/** @public */
export const campaign = defineEntity<
  Campaign,
  CampaignWireWritePayload,
  CampaignListParams,
  CampaignRelations,
  'workspace'
>({
  name: 'campaign',
  scope: 'workspace',
  pk: (c) => c.id,
  schema: { read: CampaignFromApi, write: CampaignToApi },
  relations: {
    actions: { kind: 'has-many', target: 'action', via: { on: 'target', field: 'campaign_id' } },
    tags: { kind: 'has-many', target: 'tag', via: { on: 'self', field: 'tag_ids' } },
    // The generated Campaign carries `subscription_topic_id?: string | null`
    // — a scalar FK on self, so this is `belongs-to`, not `has-many` (the
    // sideloaded `subscription_topics` on the response is a populator
    // concern, not the relation shape).
    subscription_topic: { kind: 'belongs-to', target: 'subscription_topic', via: 'subscription_topic_id' },
  },
});

/**
 * The entity's key factory plus the workflow-detail read.
 *
 * `workflowDetail(wsId, campaignId)` names the slot holding a campaign and its
 * detail-shaped actions from one `GET /campaigns/:id`, and
 * `nextProfiles(wsId, campaignId)` the people a date campaign enters next,
 * `profileMatchTime(wsId, request)` when one person would enter a date
 * campaign set up as the request describes, and `objectDatePreview(wsId, body)`
 * who an object date trigger would enter, keyed on the trigger it was asked
 * about. All sit under the entity's
 * workspace prefix, so `clearWorkspaceQueries` sweeps them with the rest.
 */
export const campaignKeys = {
  ...campaign.keys,
  /**
   * The Broadcasts API-triggered tab's list slot. Sits *under* the `list`
   * prefix so the existing list invalidations sweep it too, but in its own
   * namespace because its params and request differ from `keys.list`'s.
   */
  apiTriggeredList: (workspaceId: number, params: ApiTriggeredBroadcastListParams) =>
    [...campaign.keys.lists(workspaceId), 'api-triggered', params] as const,
  workflowDetail: (workspaceId: number, campaignId: string) =>
    [...campaign.keys.all(workspaceId), 'workflow-detail', campaignId] as const,
  nextProfiles: (workspaceId: number, campaignId: string) =>
    [...campaign.keys.all(workspaceId), 'next-profiles', campaignId] as const,
  profileMatchTime: (workspaceId: number, request: CampaignsProfileMatchTimeRequest) =>
    [...campaign.keys.all(workspaceId), 'profile-match-time', request] as const,
  objectDatePreview: (workspaceId: number, body: PreviewsObjectDatePreviewRequest) =>
    [...campaign.keys.all(workspaceId), 'object-date-preview', body] as const,
  validation: (workspaceId: number, campaignId: string) =>
    [...campaign.keys.all(workspaceId), 'validation', campaignId] as const,
  untranslatedActions: (workspaceId: number, campaignId: string) =>
    [...campaign.keys.all(workspaceId), 'untranslated-actions', campaignId] as const,
  metricsSummary: (workspaceId: number, campaignId: string) =>
    [...campaign.keys.all(workspaceId), 'metrics-summary', campaignId] as const,
  throttleChannels: (workspaceId: number) => [...campaign.keys.all(workspaceId), 'throttle-channels'] as const,
};

export function parseCampaign(raw: CampaignModel): Campaign {
  return campaign.parse(raw);
}
